import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  ArrowLeft,
  Mail,
  Calendar,
  Check,
  XCircle,
  Clock,
  Save,
  AlertTriangle,
} from "lucide-react";

interface GiftVoucher {
  id: string;
  code: string;
  amount: number;
  currency: string;
  buyerName: string;
  buyerEmail: string;
  recipientName?: string;
  recipientEmail?: string;
  personalMessage?: string;
  designTemplate: string;
  status: "active" | "used" | "expired";
  usedAt?: string;
  usedOrderId?: string;
  expiresAt: string;
  pdfUrl?: string;
  sentAt?: string;
  createdAt: string;
}

export default function VoucherDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    recipientEmail: "",
    recipientName: "",
    expiresAt: "",
    status: "" as GiftVoucher["status"],
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["voucher", id],
    queryFn: () => api.get<ApiResponse<GiftVoucher>>(`/api/admin/vouchers/${id}`),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<GiftVoucher>) => api.put(`/api/admin/vouchers/${id}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["voucher", id] });
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
      setIsEditing(false);
    },
  });

  const resendMutation = useMutation({
    mutationFn: (email?: string) =>
      api.post(`/api/admin/vouchers/${id}/resend`, email ? { email } : {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["voucher", id] });
    },
  });

  const voucher = data?.data;

  const startEditing = () => {
    if (voucher) {
      setEditData({
        recipientEmail: voucher.recipientEmail || "",
        recipientName: voucher.recipientName || "",
        expiresAt: voucher.expiresAt.split("T")[0],
        status: voucher.status,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    const updates: Partial<GiftVoucher> = {};

    if (editData.recipientEmail !== (voucher?.recipientEmail || "")) {
      updates.recipientEmail = editData.recipientEmail;
    }
    if (editData.recipientName !== (voucher?.recipientName || "")) {
      updates.recipientName = editData.recipientName;
    }
    if (editData.expiresAt !== voucher?.expiresAt.split("T")[0]) {
      updates.expiresAt = new Date(editData.expiresAt).toISOString();
    }
    if (editData.status !== voucher?.status) {
      updates.status = editData.status;
    }

    if (Object.keys(updates).length > 0) {
      updateMutation.mutate(updates);
    } else {
      setIsEditing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
            <Check className="mr-1 h-3 w-3" />
            Active
          </span>
        );
      case "used":
        return (
          <span className="badge bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            <XCircle className="mr-1 h-3 w-3" />
            Used
          </span>
        );
      case "expired":
        return (
          <span className="badge bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400">
            <Clock className="mr-1 h-3 w-3" />
            Expired
          </span>
        );
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("cs-CZ", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const isExpiringSoon = (expiresAt: string) => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return new Date(expiresAt) <= thirtyDaysFromNow && new Date(expiresAt) > new Date();
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (error || !voucher) {
    return (
      <div className="card text-error-600 text-center">
        Voucher not found or error loading data.
        <Link to="/vouchers" className="text-primary-600 mt-4 block">
          Back to vouchers
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/vouchers")} className="btn-ghost btn-sm">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Voucher {voucher.code}
            </h1>
            {getStatusBadge(voucher.status)}
          </div>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Created {new Date(voucher.createdAt).toLocaleDateString("en-US")}
          </p>
        </div>
        {!isEditing ? (
          <div className="flex gap-2">
            {voucher.status === "active" && (
              <button
                onClick={() => resendMutation.mutate(undefined)}
                disabled={resendMutation.isPending}
                className="btn-secondary"
              >
                <Mail className="h-5 w-5" />
                Resend Email
              </button>
            )}
            <button onClick={startEditing} className="btn-primary">
              Edit
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setIsEditing(false)} className="btn-ghost">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="btn-primary"
            >
              <Save className="h-5 w-5" />
              Save
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Main Info */}
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Voucher Details
          </h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Code</dt>
              <dd className="mt-1">
                <code className="rounded bg-gray-100 px-3 py-1 font-mono text-lg dark:bg-gray-700">
                  {voucher.code}
                </code>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Amount</dt>
              <dd className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(voucher.amount, voucher.currency)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
              <dd className="mt-1">
                {isEditing ? (
                  <select
                    value={editData.status}
                    onChange={(e) =>
                      setEditData({ ...editData, status: e.target.value as GiftVoucher["status"] })
                    }
                    className="input w-full"
                  >
                    <option value="active">Active</option>
                    <option value="used">Used</option>
                    <option value="expired">Expired</option>
                  </select>
                ) : (
                  getStatusBadge(voucher.status)
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Expires</dt>
              <dd className="mt-1">
                {isEditing ? (
                  <input
                    type="date"
                    value={editData.expiresAt}
                    onChange={(e) => setEditData({ ...editData, expiresAt: e.target.value })}
                    className="input w-full"
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900 dark:text-white">
                      {new Date(voucher.expiresAt).toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                    {voucher.status === "active" && isExpiringSoon(voucher.expiresAt) && (
                      <span className="text-warning-600 flex items-center gap-1 text-sm">
                        <AlertTriangle className="h-4 w-4" />
                        Expiring soon
                      </span>
                    )}
                  </div>
                )}
              </dd>
            </div>
            {voucher.usedAt && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Used At</dt>
                <dd className="mt-1 text-gray-900 dark:text-white">
                  {new Date(voucher.usedAt).toLocaleString("en-US")}
                </dd>
              </div>
            )}
            {voucher.sentAt && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Last Email Sent
                </dt>
                <dd className="mt-1 text-gray-900 dark:text-white">
                  {new Date(voucher.sentAt).toLocaleString("en-US")}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* People */}
        <div className="space-y-6">
          {/* Buyer */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Buyer</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                <dd className="mt-1 text-gray-900 dark:text-white">{voucher.buyerName}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
                <dd className="mt-1">
                  <a
                    href={`mailto:${voucher.buyerEmail}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {voucher.buyerEmail}
                  </a>
                </dd>
              </div>
            </dl>
          </div>

          {/* Recipient */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Recipient</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                <dd className="mt-1">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.recipientName}
                      onChange={(e) => setEditData({ ...editData, recipientName: e.target.value })}
                      placeholder="Recipient name"
                      className="input w-full"
                    />
                  ) : (
                    <span className="text-gray-900 dark:text-white">
                      {voucher.recipientName || "-"}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
                <dd className="mt-1">
                  {isEditing ? (
                    <input
                      type="email"
                      value={editData.recipientEmail}
                      onChange={(e) => setEditData({ ...editData, recipientEmail: e.target.value })}
                      placeholder="Recipient email"
                      className="input w-full"
                    />
                  ) : voucher.recipientEmail ? (
                    <a
                      href={`mailto:${voucher.recipientEmail}`}
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {voucher.recipientEmail}
                    </a>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Personal Message */}
      {voucher.personalMessage && (
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Personal Message
          </h2>
          <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
            {voucher.personalMessage}
          </p>
        </div>
      )}
    </div>
  );
}
